<script setup>
  import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from 'vue'
  import { useElementVisibility } from "@vueuse/core";

  const activeIndex = ref(0)
  const interval = ref(null);
  const wrapper = useTemplateRef("wrapper");
  const isVisible = useElementVisibility(wrapper);

  const htmlItems = ref(null);
  const itemsWrapperHeight = ref(300);

  const props = defineProps({
    headline: String,
    items: String
  })

  const items = computed(() => {
    return JSON.parse(props.items)
  })

  const activeItem = computed(() => {
    const content = {
      index: activeIndex.value,
      text: items.value[activeIndex.value].text,
      media: {
        element: 'range-image',
        imageUrl: items.value[activeIndex.value].media['image-url'],
        imageAlt: items.value[activeIndex.value].media['image-alt']
      }
    }
    return content
  })

  onMounted (async () => {
    await nextTick();

    htmlItems.value = document.querySelectorAll('.range-text-item-mobile')
    await setItemsWrapperHeight()
  });

  const setItemsWrapperHeight = () =>{
    const elementHeight = htmlItems.value[activeIndex.value].offsetHeight
    itemsWrapperHeight.value = elementHeight + 20
  }

  const setActiveItem = (index) => {
    activeIndex.value = index;
  };

  const setActiveItemOnClick = (index) => {
    // Break the Interval on Click
    clearInterval(interval.value);
    activeIndex.value = index;

    // Reset Autoplay after 10 Seconds
    setTimeout(() => {
      setActiveItem(activeIndex.value);
      triggerAutoplay();
    }, 15000);
  }

  watch(isVisible, () => {
    if (isVisible.value) {
      triggerAutoplay();
    }
  });

  const triggerAutoplay = () => {
    interval.value = setInterval(() => {
      if (activeIndex.value === items.value.length - 1) {
        setActiveItem(0);
        return
      }
      setActiveItem(activeIndex.value + 1);
    }, 8000)
  }
</script>
<template>
  <section
    ref="wrapper"
    class="element-range bg-secondary ~mt-16/28 ~py-16/28"
  >
    <div
      class="max-w-screen-2xl flex flex-col-reverse lg:flex-row mx-auto px-6"
    >
    <div
      class="w-full lg:w-1/2 xl:pr-20"
    >
      <p
        class="uppercase ~text-xs/sm tracking-wider"
      >
        {{ headline }}
      </p>
      <div
        class="range-navigation"
      >
        <button
          v-for="(item, index) in items"
          :key="index"
          @click="setActiveItemOnClick(index)"
          class="range-navigation-item cursor-pointer w-full flex flex-row items-center relative opacity-60 hover:opacity-100 transition-all duration-500 py-6"
        >
        <span
            class="range-navigation-item-icon overflow-hidden"
            :class="activeIndex === index ? 'is-active' : ''"
        >
          <AppIcon
            name="ArrowLink"
          />
        </span>
         <span
            class="~text-2xl/3xl text-headline"
          >
            {{ item.headline }}
          </span>
        </button>
      </div>
      <div
        class="range-text-items relative hidden lg:flex flex-col items-start lg:justify-end ~mt-10/20 w-auto"
      >
       <transition
          name="fadeLeft"
       >
           <div
             class="range-text-item"
             :key="activeItem.index"
             v-html="activeItem.text"
           />
         </transition>
      </div>
      <div
        :style="{ minHeight: `${itemsWrapperHeight}px` }"
        class="range-text-items relative flex lg:hidden flex-col items-start ~mt-10/20 w-auto"
      >
         <div
           v-for="(item, index) in items"
           :key="index"
          :class="activeIndex === index ? 'opacity-100' : 'opacity-0'"
           class="range-text-item-mobile absolute top-0 left-0 transition-all duration-300"
           v-html="item.text"
         />
      </div>
    </div>
    <div
      class="range-items relative mb-8 lg:h-[50rem] w-full lg:w-1/2 overflow-hidden"
    >
       <transition
         name="slideInLeft"
       >
           <Media
             :media="activeItem.media"
             :key="activeItem.index"
           />
       </transition>
    </div>

    </div>
  </section>
</template>

<style lang="scss" scoped>
  .range-navigation-item {
    :deep(svg) {
      @apply mr-3;
    }
    :deep(svg path) {
      fill: #3D1314 !important;
    }
    &::before {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      height: 2px;
      width: 100%;
      opacity: 60%;
      background: #3D1314;
    }
    &::after {
      content: '';
      position: absolute;
      transition: width 0.5s ease-in-out;
      bottom: 0;
      left: 0;
      height: 2px;
      width: 0;
      background: #3D1314;
    }
    &:hover {
      &::after {
        width: 100%;
      }
    }
  }
  .range-text-item, .range-text-item-mobile {
    :deep(h2) {
      @apply ~text-xl/2xl ~mb-4/6;
    }
    :deep(h3) {
      @apply ~text-lg/xl ~mb-4/6;
    }
    :deep(p) {
      @apply ~my-4/6;
      line-height: 1.8 !important;
    }
    :deep(ul li) {
      @apply ~text-sm/base py-1;
      &::before {
        @apply top-3.5 h-1 w-1;
      }
    }
  }

  .range-navigation-item-icon {
    @apply w-0 opacity-0;
    transition: width 0.3s ease-in-out 0.3s, opacity 0.3s ease-in-out;
    &.is-active {
      @apply w-8 opacity-100;
      transition: width 0.3s ease-in-out, opacity 0.3s ease-in-out 0.3s;
    }
  }

  /* Fade-in from left animation */
  .slideInLeft-enter-active {
    animation: slideInLeft 0.6s ease-out;
  }

  .slideInLeft-leave-active {
    animation: slideInLeft 0.6s ease-out reverse;
    position: absolute;
  }

  @keyframes slideInLeft {
    from {
      transform: translateX(100%);
    }
    to {
      transform: translateX(0);
    }
  }

  /* Fade out to left animation for text */
  .fadeLeft-enter-active {
    animation: fadeInFromRight 0.5s ease-out;
  }

  .fadeLeft-leave-active {
    animation: fadeOutToLeft 0.5s ease-out;
    position: absolute;
  }

  @keyframes fadeInFromRight {
    from {
      opacity: 0;
      transform: translateX(20px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes fadeOutToLeft {
    from {
      opacity: 1;
      transform: translateX(0);
    }
    to {
      opacity: 0;
      transform: translateX(-20px);
    }
  }
</style>
